import createClient from "openapi-fetch";
import type { paths } from "./schema";
import { ApiError } from "./errors";

export interface WmsApiClientOptions {
  baseUrl?: string;
  getAccessToken?: () => string | null;
  onUnauthorized?: (response: Response) => void | Promise<void>;
  fetch?: typeof globalThis.fetch;
}

/** Create a typed WMS transport with platform-owned session hooks. */
export function createWmsApiClient(options: WmsApiClientOptions = {}) {
  const api = createClient<paths>({
    baseUrl: options.baseUrl ?? "",
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });

  const notifyUnauthorized = async (response: Response) => {
    if (response.status === 401) await options.onUnauthorized?.(response);
  };

  api.use({
    onRequest({ request }) {
      const token = options.getAccessToken?.();
      if (token) request.headers.set("Authorization", `Bearer ${token}`);
      return request;
    },
    async onResponse({ response }) {
      await notifyUnauthorized(response);
      return response;
    },
  });

  const authedFetch = async (
    path: string,
    init: RequestInit = {},
  ): Promise<Response> => {
    const headers = new Headers(init.headers);
    const token = options.getAccessToken?.();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const fetchImplementation = options.fetch ?? globalThis.fetch;
    const response = await fetchImplementation(
      `${options.baseUrl ?? ""}${path}`,
      { ...init, headers },
    );
    await notifyUnauthorized(response);
    return response;
  };

  return { api, authedFetch };
}

export async function unwrap<T>(
  promise: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await promise;
  if (error !== undefined || !response.ok) throw ApiError.from(response, error);
  return data as T;
}
